import React from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

// tone -> [tint, on-colour, border, solid accent]
const TONES = {
  blue: ["--tint-blue", "--on-blue", "--tint-blue-border", "217 91% 60%"],
  green: ["--tint-green", "--on-green", "--tint-green-border", "152 62% 42%"],
  amber: ["--tint-amber", "--on-amber", "--tint-amber-border", "38 92% 50%"],
  red: ["--tint-red", "--on-red", "--tint-red-border", "0 78% 58%"],
  violet: ["--tint-violet", "--on-blue", "--tint-violet-border", "262 80% 62%"],
};

/**
 * items: [{ label, value, icon, tone, hint?, badge?: {text, up}, active?, onClick? }]
 * Shared metric cards: flat card, tinted icon tile, big number.
 */
const StatStrip = ({ items, style, min = 190 }) => (
  <div
    style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 14, marginBottom: 20, ...style }}
  >
    {items.map(({ label, value, icon: Icon, tone = "blue", hint, badge, active, onClick }, i) => {
      const [tint, on, border, solid] = TONES[tone] || TONES.blue;
      const Tag = onClick ? motion.button : motion.div;
      return (
        <Tag
          key={label}
          type={onClick ? "button" : undefined}
          onClick={onClick}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -3 }}
          className="admin-card"
          style={{
            position: "relative", overflow: "hidden", display: "block", width: "100%",
            padding: "16px 18px", textAlign: "left", fontFamily: "inherit",
            cursor: onClick ? "pointer" : "default",
            boxShadow: active ? `0 0 0 2px hsl(${solid} / 0.55)` : undefined,
          }}
        >
          <span style={{ position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 12.5, fontWeight: 500, color: "hsl(var(--muted-foreground))" }}>{label}</span>
              <span className="num" style={{ display: "block", marginTop: 6, fontSize: 24, lineHeight: 1.1, fontWeight: 700, letterSpacing: "-0.5px", color: "hsl(var(--foreground))" }}>
                {value ?? 0}
              </span>
              {hint && <span style={{ display: "block", marginTop: 4, fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>{hint}</span>}
              {badge && (
                <span
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 3, marginTop: 10, padding: "2px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 600,
                    background: badge.up ? "hsl(var(--tint-green))" : "hsl(var(--tint-red))",
                    color: badge.up ? "hsl(var(--on-green))" : "hsl(var(--on-red))",
                  }}
                >
                  {badge.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {badge.text}
                </span>
              )}
            </span>
            <span
              style={{
                width: 42, height: 42, borderRadius: 12, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                background: `hsl(var(${tint}))`, border: `1px solid hsl(var(${border}))`,
                color: `hsl(var(${on}))`,
              }}
            >
              {Icon && <Icon size={19} />}
            </span>
          </span>
        </Tag>
      );
    })}
  </div>
);

export default StatStrip;
